import { m } from "@plannello/i18n/messages";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Button as AriaButton, Form } from "react-aria-components";
import { authError, twoFactor } from "@/components/settings/authApi";
import { AuthCard } from "@/components/shell/AuthCard";
import { LocaleSwitch } from "@/components/shell/LocaleSwitch";
import { Button, Checkbox, CodeField, InlineError, PasswordField, TextField } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { signIn } from "@/lib/auth-client";
import { clearDrafts, useDraft } from "@/lib/draft";
import { focusFirstInvalid } from "@/lib/forms";
import { applyLocale } from "@/lib/locale";
import { loadMe, loadSetupStatus, meQuery, setupStatusQuery } from "@/lib/queries";
import { describeError } from "@/lib/session";

/** Only same-origin absolute paths are accepted as post-login targets. */
function safeRedirect(target: string | undefined): string {
  if (target?.startsWith("/") && !target.startsWith("//") && !target.startsWith("/login")) return target;
  return "/";
}

type Providers = { password: boolean; oidc: { enabled: boolean; providerId: string; displayName: string | null } };
const providersQuery = {
  queryKey: ["auth", "providers"],
  queryFn: () => api.get<Providers>("/api/v1/auth/providers"),
  staleTime: 60_000,
  retry: false,
} as const;

/** Friendly sentences for the error codes the SSO callback puts in `/login?error=`. */
function ssoError(code: string): string {
  switch (code) {
    case "account_not_linked":
      return m.sso_err_account_not_linked();
    case "unable_to_create_session":
      return m.sso_err_unable_to_create_session();
    case "signup_disabled":
      return m.sso_err_signup_disabled();
    case "unable_to_get_user_info":
      return m.sso_err_unable_to_get_user_info();
    case "sso_sync_failed":
      return m.sso_err_sync_failed();
    default:
      return m.sso_err_generic();
  }
}

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): { redirect?: string; error?: string; twoFactor?: boolean } => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
    error: typeof s.error === "string" ? s.error : undefined,
    // The SSO callback sets the two-factor cookie and sends the person here to finish with a code.
    twoFactor: s.twoFactor === "1" || s.twoFactor === 1 || s.twoFactor === true ? true : undefined,
  }),
  beforeLoad: async ({ context }) => {
    const status = await loadSetupStatus(context.queryClient);
    if (status?.needsSetup) throw redirect({ to: "/setup" });
    try {
      if (await loadMe(context.queryClient)) throw redirect({ to: "/" });
    } catch (e) {
      if (e instanceof ApiError) return; // API down: render the form, sign-in will report the error
      throw e;
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const router = useRouter();
  const qc = useQueryClient();
  const { redirect: target, error: ssoCode, twoFactor: afterSso } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [email, setEmail] = useDraft("login.email", "");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string; form?: string }>({});
  const [pending, setPending] = useState(false);
  const [challenge, setChallenge] = useState(afterSso === true);
  // True while the code step belongs to an SSO sign-in (no password was typed here).
  const [viaSso, setViaSso] = useState(afterSso === true);
  const instance = useQuery(setupStatusQuery).data?.instanceName?.trim();
  const providers = useQuery(providersQuery).data;

  /** Session exists: load the account, apply its language, go where the person was headed. */
  async function finish() {
    clearDrafts("login.");
    qc.removeQueries({ queryKey: meQuery.queryKey });
    const me = await qc.fetchQuery(meQuery);
    applyLocale(me.locale);
    router.history.push(safeRedirect(target));
  }

  async function submit() {
    const next: typeof errors = {};
    if (!email.trim()) next.email = m.validation_required();
    if (!password) next.password = m.validation_required();
    setErrors(next);
    if (next.email || next.password) {
      focusFirstInvalid();
      return;
    }

    setPending(true);
    try {
      const { data, error } = await signIn.email({ email: email.trim(), password });
      if (error) {
        const status = error.status ?? 0;
        const invalid =
          error.code === "INVALID_EMAIL_OR_PASSWORD" || status === 401 || status === 400 || status === 403;
        setErrors({
          form: invalid
            ? m.auth_invalid()
            : status === 429
              ? m.error_rate_limited()
              : describeError(new ApiError(status, "auth", error.message ?? ""), {
                  network: m.error_network(),
                  generic: m.error_generic(),
                }),
        });
        return;
      }
      if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
        setChallenge(true);
        return;
      }
      await finish();
    } catch (e) {
      setErrors({ form: describeError(e, { network: m.error_network(), generic: m.error_generic() }) });
    } finally {
      setPending(false);
    }
  }

  async function startSso() {
    if (!providers?.oidc.enabled) return;
    setPending(true);
    try {
      const res = await api.post<{ url?: string }>("/api/auth/sign-in/social", {
        provider: providers.oidc.providerId,
        callbackURL: safeRedirect(target),
        errorCallbackURL: "/login",
      });
      if (res.url) window.location.assign(res.url);
      else setErrors({ form: m.sso_err_generic() });
    } catch (e) {
      setErrors({ form: describeError(e, { network: m.error_network(), generic: m.sso_err_generic() }) });
    } finally {
      setPending(false);
    }
  }

  if (challenge) {
    return (
      <TwoFactorStep
        viaSso={viaSso}
        onDone={finish}
        onBack={() => {
          setChallenge(false);
          setViaSso(false);
          setPassword("");
          if (afterSso) void navigate({ search: (prev) => ({ ...prev, twoFactor: undefined }), replace: true });
        }}
      />
    );
  }

  const ssoName = providers?.oidc.displayName ?? "SSO";
  return (
    <AuthCard
      title={instance ? m.login_instance({ instance }) : m.login_title()}
      intro={m.login_intro()}
      footer={<LocaleSwitch />}
    >
      <Form
        validationBehavior="aria"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {ssoCode ? <InlineError message={ssoError(ssoCode)} /> : null}
        {errors.form ? <InlineError message={errors.form} /> : null}
        <TextField
          label={m.auth_email()}
          name="email"
          type="email"
          autoComplete="username"
          inputMode="email"
          autoFocus
          value={email}
          onChange={setEmail}
          error={errors.email}
        />
        <PasswordField
          label={m.auth_password()}
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          error={errors.password}
        />
        <Button type="submit" variant="primary" size="lg" isPending={pending} className="mt-2 w-full">
          {m.auth_sign_in()}
        </Button>
        {providers?.oidc.enabled ? (
          <>
            <div className="flex items-center gap-3 text-xs text-fg-subtle">
              <span className="h-px flex-1 bg-line" />
              <span>{m.sso_or()}</span>
              <span className="h-px flex-1 bg-line" />
            </div>
            <Button
              variant="secondary"
              size="lg"
              isDisabled={pending}
              onPress={() => void startSso()}
              className="w-full"
            >
              {m.sso_continue({ name: ssoName })}
            </Button>
          </>
        ) : null}
      </Form>
    </AuthCard>
  );
}

/** Second step after a correct password: authenticator code, or a backup code, optionally trusting the device. */
function TwoFactorStep({
  viaSso,
  onDone,
  onBack,
}: {
  viaSso?: boolean;
  onDone: () => Promise<void>;
  onBack: () => void;
}) {
  const [backup, setBackup] = useState(false);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);

  async function submit() {
    if (!code) {
      setError(m.validation_required());
      focusFirstInvalid();
      return;
    }
    setPending(true);
    setError(undefined);
    try {
      if (backup) await twoFactor.verifyBackup(code);
      else await twoFactor.verify(code, trust);
      await onDone();
    } catch (e) {
      // The challenge cookie lasts a few minutes; after that the password has to be entered again.
      setError(
        e instanceof ApiError && e.code === "INVALID_TWO_FACTOR_COOKIE"
          ? viaSso
            ? m.login_2fa_sso_expired()
            : m.login_2fa_expired()
          : authError(e),
      );
      focusFirstInvalid();
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthCard
      title={m.login_2fa_title()}
      intro={backup ? m.login_2fa_backup_intro() : viaSso ? m.login_2fa_sso_intro() : m.login_2fa_intro()}
      footer={<LocaleSwitch />}
    >
      <Form
        validationBehavior="aria"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        <CodeField
          key={backup ? "backup" : "totp"}
          kind={backup ? "backup" : "totp"}
          label={backup ? m.login_2fa_backup_label() : m.sec_2fa_code()}
          name="code"
          autoFocus
          value={code}
          onChange={(v) => {
            setCode(v);
            setError(undefined);
          }}
          error={error}
        />
        {backup ? null : (
          <Checkbox isSelected={trust} onChange={setTrust} description={m.login_2fa_trust_hint()}>
            {m.login_2fa_trust()}
          </Checkbox>
        )}
        <Button type="submit" variant="primary" size="lg" isPending={pending} className="mt-1 w-full">
          {m.auth_sign_in()}
        </Button>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <AriaButton
            onPress={() => {
              setBackup(!backup);
              setCode("");
              setError(undefined);
            }}
            className="rounded-control px-1 text-accent-text underline underline-offset-2 outline-none data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus"
          >
            {backup ? m.login_2fa_use_app() : m.login_2fa_use_backup()}
          </AriaButton>
          <AriaButton
            onPress={onBack}
            className="rounded-control px-1 text-fg-muted underline underline-offset-2 outline-none data-hovered:text-fg data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus"
          >
            {m.login_2fa_back()}
          </AriaButton>
        </div>
      </Form>
    </AuthCard>
  );
}
